import FormLayout from 'src/app/layout/form-layout/form-layout'
import AttachesGrid from './attaches-grid'
import UploadFile from './upload-attaches'
import { ReservationFileFragment, useFileUploadPresignedURLMutation, useReservationUpdateMutation } from 'src/api/api.generated'
import { useReducerGeneric } from 'src/app/hooks/reducer-generic/reducer-generic'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import { AttachesTabState, FileState, FileTypeAccepted, isImage } from './attaches.type'
import { useConfirm } from 'hic-components'
import { useTranslation } from 'react-i18next'
import { PromiseCompressor, QUALITY_COMPRESSION, getNewDimension } from 'src/utils/compressorjs'

function convertFileFragmentToFileState(files: ReservationFileFragment[]): FileState[] {
  return files.map((file) => ({
    id: file.id,
    name: file.filename ?? '',
    type: (file.mimeType ?? '') as FileTypeAccepted,
    createOn: file.auditable.createdOn ?? new Date(),
    URL: file.URL ?? '',
  }))
}

const MEGA_BYTE = 1024 * 1024

export default function AttachesTab({ files, propertyId, tenantId, reservationId }: { files: ReservationFileFragment[]; tenantId: string; propertyId: string; reservationId: string }) {
  const showError = useErrorSnackBar()
  const [fileUploadMutation] = useFileUploadPresignedURLMutation()
  const [updateReservationMutation] = useReservationUpdateMutation()
  const { t } = useTranslation()
  const confirm = useConfirm()
  const [state, stateSubstitute] = useReducerGeneric<AttachesTabState>({
    initial_state_params: {
      ui: {
        loading: false,
      },
      files: convertFileFragmentToFileState(files),
    },
  })

  async function updateFilesReservation(fileIds: string[]) {
    const { data } = await updateReservationMutation({
      variables: {
        id: reservationId,
        input: {
          fileIds: fileIds,
          tenantId,
        },
      },
    })

    const updatedFiles = data?.updateReservation?.files

    if (!updatedFiles) {
      throw new Error('error updating reservation with new file')
    }

    stateSubstitute('files', convertFileFragmentToFileState(updatedFiles))
  }

  async function uploadFile(files: FileList | null) {
    if (!files) return

    try {
      stateSubstitute('ui:loading', true)

      for (let file of files) {
        if (isImage(file.type as FileTypeAccepted)) {
          let width = 0
          let height = 0
          const image = new Image()
          image.src = URL.createObjectURL(file)

          await new Promise((resolve) => {
            image.onload = () => {
              width = image.width
              height = image.height
              resolve(true)
            }
          })

          const newDimension = getNewDimension({ width, height })
          file = await PromiseCompressor({
            file,
            options: { quality: QUALITY_COMPRESSION, convertTypes: ['image/png', 'image/jpeg'], mimeType: 'image/jpeg', convertSize: Infinity, checkOrientation: false, ...newDimension },
          })
        }

        if (file.size > 10 * MEGA_BYTE) {
          throw new Error(t('pms.booking.reservation.tab.attaches.upload.error.max_size'))
        }

        const { data } = await fileUploadMutation({
          variables: {
            file: {
              filename: file.name,
              public: false,
              mimeType: file.type,
              tenantId,
              propertyId,
            },
          },
        })

        if (!data?.fileUploadPresignedURL?.preSignedURL || !data.fileUploadPresignedURL.file.id) {
          throw new Error('missing preSignedURL or file id')
        }

        await fetch(data?.fileUploadPresignedURL.preSignedURL, {
          method: 'PUT',
          headers: { 'Content-Type': file.type },
          body: file,
        })

        await updateFilesReservation([data.fileUploadPresignedURL.file.id, ...state.files.map((file) => file.id)])
      }
    } catch (e) {
      console.error(e)
      showError(e)
    } finally {
      stateSubstitute('ui:loading', false)
    }
  }

  function confirmDeleteFile(fileId: string) {
    confirm({
      title: t('pms.booking.reservation.tab.attaches.delete_title'),
      message: t('pms.booking.reservation.tab.attaches.delete_message'),
      confirmButtonText: t('commons.ok'),
      cancelButtonText: t('commons.cancel'),
      onConfirm: () => deleteFile(fileId),
    })
  }

  async function deleteFile(fileId: string) {
    try {
      stateSubstitute('ui:loading', true)

      await updateFilesReservation(state.files.filter((file) => file.id !== fileId).map((file) => file.id))
    } catch (e) {
      console.error(e)
      showError(e)
    } finally {
      stateSubstitute('ui:loading', false)
    }
  }

  return (
    <FormLayout
      title={''}
      appearance={{
        type: 'RAW',
      }}
      toolbar={<></>}
    >
      <UploadFile onUpload={uploadFile} />
      <AttachesGrid loading={state.ui.loading} rows={state.files} onDeleteClick={confirmDeleteFile} />
    </FormLayout>
  )
}
